/* eslint-disable sonarjs/cognitive-complexity */
import {useStyles} from '@wix/tpa-settings/react';
import {SlotsPlaceholder} from '@wix/widget-plugins-ooi';
import {ChevronDown, ChevronUp} from '@wix/wix-ui-icons-common/on-stage';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {useEnvironment} from '@wix/yoshi-flow-editor';
import classNames from 'classnames';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Text, TextButton} from '@wix/wix-ui-tpa/cssVars';
import {ConditionalRender} from '../../../common/components/ConditionalRender/ConditionalRender';
import {CategoryListLayout, GallerySlotIds} from '../../../constants';
import {IGalleryGlobalProps} from '../../../gallery/galleryGlobalStrategy';
import {useGlobals, withGlobals} from '../../../globalPropsContext';
import {useStylesParams} from '../../../stylesParamsContext';
import {useCategories} from '../../hooks/useCategories';
import {useSlots} from '../../hooks/useSlots';
import {CategoryListHeader} from './CategoryListHeader/CategoryListHeader';
import {CategoryListItem} from './CategoryListItem/CategoryListItem';
import s from './CategoryTree.scss';
import {classes as stylableClasses} from './CategoryTree.st.css';

export const CategoryTreeDataHook = {
  SectionTitle: 'category-tree-section-title',
  List: 'category-tree-list',
  BackButton: 'category-tree-back-button',
  ShowMoreButton: 'category-tree-show-more-button',
  CategoryHeader: 'category-tree-header',
  Container: 'category-tree-container',
  ShopAllButton: 'category-tree-shop-all-button',
  CategoryOption: {
    at: (i: number) => `category-option-${i}`,
  },
};

export const CategoryTree: React.FunctionComponent<IGalleryGlobalProps & IProvidedTranslationProps> = withGlobals(
  withTranslations()(() => {
    const {isMobile} = useEnvironment();
    const styles = useStyles();
    const stylesParams = useStylesParams();
    const categoryListLayout = styles.get(stylesParams.gallery_categoryListLayout);
    const isVerticalLayout = categoryListLayout === CategoryListLayout.Vertical && !isMobile;
    const [categoryListCollapsed, setCategoryListCollapsed] = useState<boolean>(isVerticalLayout);
    const {textsMap, htmlTags} = useGlobals();
    const {categoryTreeTitle} = textsMap;
    const {categoriesFiltersHtmlTag} = htmlTags;
    const {categoryList: categories, listHeaderCategory, reportShowMoreOrLessClicked} = useCategories();
    const categoryListRef = useRef<HTMLUListElement>();

    /* istanbul ignore next - hard to test with js dom */
    useEffect(() => {
      if (!categoryListRef.current) {
        return;
      }

      const {offsetTop: categoryListTop, clientHeight: categoryListHeight} = categoryListRef.current;
      const categoryListBottomPosition = categoryListTop + categoryListHeight;
      const categoryListItemElements = [...categoryListRef.current.querySelectorAll('li')] as HTMLLIElement[];

      categoryListItemElements.forEach((li) => ((li.firstChild as any).tabIndex = 0));
      const overflowHiddenListItems = categoryListItemElements.filter(
        (li) => li.offsetTop > categoryListBottomPosition
      );

      overflowHiddenListItems.forEach((li) => ((li.firstChild as any).tabIndex = -1));
    }, [categoryListRef, categoryListCollapsed, categories]);

    const toggleCategoryList = useCallback(() => {
      setCategoryListCollapsed((isCollapsed) => {
        reportShowMoreOrLessClicked(isCollapsed);
        return !isCollapsed;
      });
    }, [reportShowMoreOrLessClicked]);

    const shouldIndentCategoryList = listHeaderCategory;
    const inlineCssVars = {'--totalNumberOfCategories': categories.length};

    const {isSlotUsed} = useSlots();
    const topSlotUsed = isSlotUsed(GallerySlotIds.CategoryPageListTop);

    return (
      <>
        <div className={classNames({[s.topSlot]: topSlotUsed})}>
          <SlotsPlaceholder slotId={GallerySlotIds.CategoryPageListTop} />
        </div>

        <div
          className={classNames(s.root, {
            [s.horizontalLayout]: !isVerticalLayout,
          })}
          data-hook={CategoryTreeDataHook.Container}>
          {isVerticalLayout && (
            <>
              <ConditionalRender by={'gallery_showCategoriesTitle'}>
                <Text data-hook={CategoryTreeDataHook.SectionTitle} tagName={categoriesFiltersHtmlTag}>
                  <span className={s.sectionTitle}>{categoryTreeTitle}</span>
                </Text>
              </ConditionalRender>
              {listHeaderCategory && (
                <ConditionalRender by={'gallery_showCategoryListHeader'} className={s.listHeader}>
                  <CategoryListHeader dataHook={CategoryTreeDataHook.CategoryHeader} />
                </ConditionalRender>
              )}
            </>
          )}
          <div
            data-hook={CategoryTreeDataHook.List}
            style={inlineCssVars as React.CSSProperties}
            className={s.listContainer}>
            <ul
              ref={categoryListRef}
              className={classNames(s.categoryList, {
                [s.categoryListCollapsed]: categoryListCollapsed,
                [s.shouldIndent]: shouldIndentCategoryList,
              })}>
              {listHeaderCategory && (
                <ConditionalRender by={'gallery_showCategoryListShopAll'} className={s.listItem}>
                  <CategoryListItem
                    key={listHeaderCategory.id}
                    dataHook={CategoryTreeDataHook.ShopAllButton}
                    category={listHeaderCategory}
                    text={textsMap.categoryListShopAllButton}
                  />
                </ConditionalRender>
              )}
              {categories.map((category, i) => (
                <CategoryListItem
                  key={category.id}
                  className={s.listItem}
                  dataHook={CategoryTreeDataHook.CategoryOption.at(i)}
                  category={category}
                  text={category.name === 'All Products' ? textsMap.allProducts : category.name}
                />
              ))}
            </ul>
            {isVerticalLayout && (
              <ConditionalRender by={'shouldLimitCategoryList'}>
                <TextButton
                  suffixIcon={categoryListCollapsed ? <ChevronDown /> : <ChevronUp />}
                  data-hook={CategoryTreeDataHook.ShowMoreButton}
                  onClick={() => toggleCategoryList()}
                  className={classNames(stylableClasses.showMoreCategoriesTextButtonRoot, s.showMore, {
                    [s.shouldIndent]: shouldIndentCategoryList,
                  })}>
                  {categoryListCollapsed ? textsMap.categoryListShowMore : textsMap.categoryListShowLess}
                </TextButton>
              </ConditionalRender>
            )}
          </div>
        </div>
      </>
    );
  })
);
